import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import PersonData from './personData';
import moment from 'moment';
import Validator from '../../../../util/validator';
import config from '../../../../app/config';
import _ from 'lodash';
import {NHSStatusCode, WithheldIdentityReason} from '../../../../controls/agyle/dictionaries';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {nhsNoVerificationStatuses, withheldIdentityReason} from '../../../../store/reducers/dicts';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import {cleanObject} from '../../../../util/patientContextUtil';

const Identity = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    const {isSmall} = props;

    // Patient identity Dataset
    const [patientName, setPatientName] = useState('');
    const [patientUsualAddress, setPatientUsualAddress] = useState('');
    const [patientIdentity, setPatientIdentity] = useState({
        NHSNumberStatusIndicatorCode: null,
        OrganisationIdentifier_ResidenceResponsibility: '',
        WithheldIdentityReason: null,
        LocalPatientIdentifier_Extended: '',
        OrganisationIdentifier_LocalPatientIdentifier: '',
        NHSNumber: '',
        PostcodeOfUsualAddress: '',
        PersonBirthDate: null,
        PatientName: patientName,
        PatientUsualAddress: patientUsualAddress,
    });

    // Dictionaries
    const nhsNoVerificationStatusesDict = useSelector(nhsNoVerificationStatuses);
    const withheldIdentityReasonDict = useSelector(withheldIdentityReason);

    // Check NHS Code Status From PAS
    const [nhsStatus, setNhsStatus] = useState(props.patient.patient.nhsNumberVerifiedStatus);

    // For ReadOnly Data
    const disableData = useRef({});

    // invalid pickers
    const errorPicker = useRef(null);

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        orgIDRR: false,
        withheldReason: false,
        NHSCode: validationEnabled,
        NHSNumber: false,
        patientId: validationEnabled,
        orgIDLPI: validationEnabled,
        postCode: false,
    });

    // Initial Data
    useEffect(() => {
        let data = {};
        let dataToBlock = {};
        if (props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientIdentity) {
            const clone = _.cloneDeep(props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientIdentity);
            if (clone?.VerifiedIdentity_Structure) {
                data = {
                    ...clone?.VerifiedIdentity_Structure?.LocalIdentifierStructure,
                    ...clone?.VerifiedIdentity_Structure?.DataElementStructure,
                };
                data = {
                    ...data,
                    NHSNumberStatusIndicatorCode: data?.NHSNumberStatusIndicatorCode ?
                        _.find(nhsNoVerificationStatusesDict, {__id: data?.NHSNumberStatusIndicatorCode}) : null,
                };
            } else if (clone?.UnverifiedIdentity_Structure) {
                data = {
                    ...clone?.UnverifiedIdentity_Structure?.LocalIdentifierStructure,
                    ...clone?.UnverifiedIdentity_Structure?.DataElementStructure,
                    ...clone?.UnverifiedIdentity_Structure?.DataElementStructure2,
                };
                data = {
                    ...data,
                    NHSNumberStatusIndicatorCode: data?.NHSNumberStatusIndicatorCode ?
                        _.find(nhsNoVerificationStatusesDict, {__id: data?.NHSNumberStatusIndicatorCode}) : null,
                };
            } else {
                data = {...clone?.WithheldIdentity_Structure};
                data = {
                    ...data,
                    NHSNumberStatusIndicatorCode: data?.NHSNumberStatusIndicatorCode ?
                        _.find(nhsNoVerificationStatusesDict, {__id: data?.NHSNumberStatusIndicatorCode}) : null,
                    WithheldIdentityReason: data?.WithheldIdentityReason ?
                        _.find(withheldIdentityReasonDict, {__code: data?.WithheldIdentityReason}) : null,
                };
            }
        }

        if (props.patient.patient) {
            dataToBlock = {
                NHSNumberStatusIndicatorCode: props.patient.patient.nhsNumberVerifiedStatus ?
                    _.find(nhsNoVerificationStatusesDict, {__id: props.patient.patient.nhsNumberVerifiedStatus}) : null,
                LocalPatientIdentifier_Extended: props.patient.patient.__id || '',
                NHSNumber: props.patient.patient.nhsNumber || '',
                PostcodeOfUsualAddress: props.patient.patient.postalCode || '',
                PersonBirthDate: props.patient.patient.dob || null,
            };
        }

        data = {
            ...data,
            ...dataToBlock,
        };

        if (validationEnabled) {
            const error = {
                NHSNumber: !data?.NHSNumber ? data.NHSNumberStatusIndicatorCode === '01' : false,
                postCode: !data?.PostcodeOfUsualAddress ? data.NHSNumberStatusIndicatorCode === '01' : false,
            };
            setError(prevState => ({...prevState, ...error}));
        }
        setPatientIdentity(prevState => ({...prevState, ...data}));
        disableData.current = {...cleanObject(dataToBlock)};
    }, []);

    useEffect(() => {
        setNhsStatus(patientIdentity.NHSNumberStatusIndicatorCode);
    }, [patientIdentity?.NHSNumberStatusIndicatorCode]);

    const updateData = () => {
        let data;
        switch (patientIdentity.NHSNumberStatusIndicatorCode) {
            case '01':
                data = {
                    VerifiedIdentity_Structure: {
                        LocalIdentifierStructure: {
                            LocalPatientIdentifier_Extended: patientIdentity.LocalPatientIdentifier_Extended,
                            OrganisationIdentifier_LocalPatientIdentifier: patientIdentity.OrganisationIdentifier_LocalPatientIdentifier,
                        },
                        DataElementStructure: {
                            NHSNumber: patientIdentity.NHSNumber,
                            NHSNumberStatusIndicatorCode: patientIdentity.NHSNumberStatusIndicatorCode?.__id || '',
                            PostcodeOfUsualAddress: patientIdentity.PostcodeOfUsualAddress,
                            OrganisationIdentifier_ResidenceResponsibility: patientIdentity.OrganisationIdentifier_ResidenceResponsibility,
                            PersonBirthDate: patientIdentity.PersonBirthDate ? moment(patientIdentity.PersonBirthDate).format(config.ecdsDateFormat) : '',
                        },
                    },
                    NHSNumberStatusIndicatorCode: patientIdentity.NHSNumberStatusIndicatorCode ? patientIdentity.NHSNumberStatusIndicatorCode?.__id : '',
                };
                break;
            case '':
            case null:
                data = {
                    WithheldIdentity_Structure: {
                        NHSNumberStatusIndicatorCode: patientIdentity.NHSNumberStatusIndicatorCode?.__id || '',
                        OrganisationIdentifier_ResidenceResponsibility: patientIdentity.OrganisationIdentifier_ResidenceResponsibility,
                        WithheldIdentityReason: patientIdentity.WithheldIdentityReason?.__code || '',
                    },
                    NHSNumberStatusIndicatorCode: patientIdentity.NHSNumberStatusIndicatorCode?.__id || '',
                };
                break;
            default:
                data = {
                    UnverifiedIdentity_Structure: {
                        LocalIdentifierStructure: {
                            LocalPatientIdentifier_Extended: patientIdentity.LocalPatientIdentifier_Extended,
                            OrganisationIdentifier_LocalPatientIdentifier: patientIdentity.OrganisationIdentifier_LocalPatientIdentifier,
                        },
                        DataElementStructure: {
                            NHSNumber: patientIdentity.NHSNumber,
                            NHSNumberStatusIndicatorCode: patientIdentity.NHSNumberStatusIndicatorCode?.__id || '',
                            PatientName: patientName,
                            PatientUsualAddress: patientUsualAddress,
                        },
                        DataElementStructure2: {
                            PostcodeOfUsualAddress: patientIdentity.PostcodeOfUsualAddress,
                            OrganisationIdentifier_ResidenceResponsibility: patientIdentity.OrganisationIdentifier_ResidenceResponsibility,
                            PersonBirthDate: patientIdentity.PersonBirthDate ? moment(patientIdentity.PersonBirthDate).format(config.ecdsDateFormat) : '',
                        },
                    },
                    NHSNumberStatusIndicatorCode: patientIdentity.NHSNumberStatusIndicatorCode?.__id || '',
                };
                break;
        }
        props.setData(data);
    };

    useEffect(() => {
        if (validate()) {
            updateData();
        }
    }, [patientIdentity, patientName, patientUsualAddress]);

    const changeHandler = (event) => {
        setPatientIdentity((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const validate = () => {
        let check = true;
        if (validationEnabled) {
            let dataError = {
                orgIDRR: patientIdentity.OrganisationIdentifier_ResidenceResponsibility === '' ? false : !props.checkLenght(3, 5, patientIdentity.OrganisationIdentifier_ResidenceResponsibility),
                patientId: !props.checkLenght(1, 20, patientIdentity.LocalPatientIdentifier_Extended),
                orgIDLPI: !props.checkLenght(3, 5, patientIdentity.OrganisationIdentifier_LocalPatientIdentifier),
                withheldReason: patientIdentity.WithheldIdentityReason === '' ? !props.checkLenght(2, 2, patientIdentity.WithheldIdentityReason?.__code) : false,
                NHSCode: !props.checkLenght(2, 2, patientIdentity.NHSNumberStatusIndicatorCode?.__id),
            };
            if (patientIdentity.NHSNumberStatusIndicatorCode?.__id === '01') {
                dataError = {
                    ...dataError,
                    NHSNumber: !props.checkLenght(10, 10, patientIdentity.NHSNumber),
                    PostCode: !Validator.validPostCode(patientIdentity.PostcodeOfUsualAddress),
                };
            } else {
                dataError = {
                    ...dataError,
                    NHSNumber: patientIdentity.NHSNumber === '' ? false : !props.checkLenght(10, 10, patientIdentity.NHSNumber),
                    PostCode: patientIdentity.PostcodeOfUsualAddress === '' ? false : !Validator.validPostCode(patientIdentity.PostcodeOfUsualAddress),
                };
            }
            setError(prevState => ({...prevState, ...dataError}));

            for (let prop in dataError) {
                if (dataError[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Patient Identity',
                    groupStatus: 'M',
                    mandatory: !patientIdentity?.LocalPatientIdentifier_Extended || !patientIdentity?.OrganisationIdentifier_LocalPatientIdentifier
                        || !patientIdentity?.NHSNumberStatusIndicatorCode || !patientIdentity?.PostcodeOfUsualAddress
                        || (patientIdentity.NHSNumberStatusIndicatorCode === '01' ? !patientIdentity?.NHSNumber : false),
                    errors: !check,
                    pickers: errorPicker.current !== null,
                };
                props.valid(prevState => ({...prevState, 'identity': validObj}));
            }
            return check && !errorPicker.current;
        } else {
            return check;
        }
    };

    const dateHandler = (name, event) => {
        const date = moment(event).format(config.ecdsDateFormat);
        setPatientIdentity((prevState) => ({...prevState, [name]: date}));
    };

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    if (props.accordion !== 'Patient Identity') return null;

    return (
        <div className={classes.root}>
            {!nhsStatus &&
                <Grid container spacing={2} style={{marginTop: 1}}>
                    <Grid item xs={12}>
                        <NHSStatusCode
                            name="NHSNumberStatusIndicatorCode"
                            label={t('NHS Status Indicator Code')}
                            value={patientIdentity.NHSNumberStatusIndicatorCode}
                            onChange={(_e, val) => setDataDictionary(['NHSNumberStatusIndicatorCode', val])}
                            error={error.NHSCode}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleTextField
                            name="OrganisationIdentifier_ResidenceResponsibility"
                            label={t('Org id - Residence Responsibility')}
                            onChange={changeHandler}
                            value={patientIdentity.OrganisationIdentifier_ResidenceResponsibility}
                            error={error.orgIDRR}
                            helperText={error.orgIDRR ? 'Input must be between 3 and 5 characters long.' : ''}
                            maxLength={5}
                            minLength={3}
                            readOnly={!!disableData?.current.OrganisationIdentifier_ResidenceResponsibility}
                            fullWidth
                            debounce
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <WithheldIdentityReason
                            name="WithheldIdentityReason"
                            label={t('Withheld Reason')}
                            color="primary"
                            value={patientIdentity.WithheldIdentityReason}
                            onChange={(_e, val) => setDataDictionary(['WithheldIdentityReason', val])}
                            disabled={!!disableData?.current.WithheldIdentityReason}
                            error={error.withheldReason}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>
                </Grid>
            }

            {nhsStatus &&
                <>
                    <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                        <Grid item xs={12}>
                            <NHSStatusCode
                                name="NHSNumberStatusIndicatorCode"
                                label={t('NHS Status Indicator Code')}
                                value={patientIdentity.NHSNumberStatusIndicatorCode}
                                onChange={(_e, val) => setDataDictionary(['NHSNumberStatusIndicatorCode', val])}
                                disabled={!!disableData?.current.NHSNumberStatusIndicatorCode}
                                error={error.NHSCode}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleTextField
                                name="LocalPatientIdentifier_Extended"
                                label={t('Local Patient Identifier')}
                                onChange={changeHandler}
                                value={patientIdentity.LocalPatientIdentifier_Extended}
                                error={error.patientId}
                                helperText={error.patientId ? 'Input must be max 20 characters long.' : ''}
                                maxLength={20}
                                readOnly={!!disableData?.current.LocalPatientIdentifier_Extended}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleTextField
                                name="NHSNumber"
                                label={t('NHS Number')}
                                type="number"
                                onChange={changeHandler}
                                value={patientIdentity.NHSNumber}
                                error={error.NHSNumber}
                                helperText={error.NHSNumber ? 'Input must be 10 characters long.' : ''}
                                maxLength={10}
                                minLength={10}
                                readOnly={!!disableData?.current.NHSNumber}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleDatePicker
                                name="PersonBirthDate"
                                label={t('Person Birth Date')}
                                value={patientIdentity.PersonBirthDate}
                                type="date"
                                onChange={(e) => dateHandler('PersonBirthDate', e)}
                                minDate={props.patient?.patient?.dob ? moment(props.patient.patient.dob) : null}
                                maxDate={props.patient?.patient?.dob ? moment(props.patient.patient.dob) : null}
                                onError={event => errorPicker.current = event}
                                readOnly={!!disableData?.current.PersonBirthDate}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleTextField
                                name="PostcodeOfUsualAddress"
                                label={t('Postcode')}
                                onChange={changeHandler}
                                value={patientIdentity.PostcodeOfUsualAddress || ''}
                                error={error.postCode}
                                helperText={error.postCode ? 'Post Code must follow the structure of NHSPD' : ''}
                                maxLength={8}
                                minLength={2}
                                readOnly={!!disableData?.current.PostcodeOfUsualAddress}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleTextField
                                name="OrganisationIdentifier_LocalPatientIdentifier"
                                label={t('Org Id - Local Patient Identifier')}
                                onChange={changeHandler}
                                value={patientIdentity.OrganisationIdentifier_LocalPatientIdentifier || ''}
                                error={error.orgIDLPI}
                                helperText={error.orgIDLPI ? 'Input must be between 3 and 5 characters long.' : ''}
                                maxLength={5}
                                minLength={3}
                                readOnly={!!disableData?.current.OrganisationIdentifier_LocalPatientIdentifier}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleTextField
                                name="OrganisationIdentifier_ResidenceResponsibility"
                                label={t('Org Id - Residence Responsibility')}
                                onChange={changeHandler}
                                value={patientIdentity.OrganisationIdentifier_ResidenceResponsibility || ''}
                                error={error.orgIDRR}
                                helperText={error.orgIDRR ? 'Input must be between 3 and 5 characters long.' : ''}
                                maxLength={5}
                                minLength={3}
                                readOnly={!!disableData?.current.OrganisationIdentifier_ResidenceResponsibility}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    </Grid>
                    {patientIdentity.NHSNumberStatusIndicatorCode !== '' && patientIdentity.NHSNumberStatusIndicatorCode !== '01' &&
                        <PersonData
                            setName={setPatientName}
                            setAddress={setPatientUsualAddress}
                            patient={props.patient}
                            isSmall={isSmall}
                        />
                    }
                </>
            }
        </div>
    );
};

export default Identity;
